import isEqual from "lodash.isequal";

import { v4 } from "uuid";

import {
  SharedTextComponent,
  SharedTextComponentSchema,
} from "@intelligems/ig-types";
import { sharedFromAiFormat } from "@src/store/slices/experienceBuilder/common/utils/sharedFromAiFormat";
import { pickSharedFields } from "@src/store/slices/experienceBuilder/common/utils/pickSharedFields";
import { getDefaultText } from "@src/store/slices/experienceBuilder/component/defaults";
import {
  ModHandler,
  TextComponent,
} from "@src/store/slices/experienceBuilder/types";

import { escapeAttr as esc } from "./escapeAttr";
import { isValidSelector } from "@src/store/slices/experienceBuilder/common/utils/html/isValidSelector";

function toHtml(mod: TextComponent): string {
  return `<ig-text-component
    line-height="${esc(mod.lineHeight)}"
    letter-spacing="${esc(mod.letterSpacing)}"
    padding="${esc(mod.padding)}"
    margin="${esc(mod.margin)}"
  >${mod.content}</ig-text-component>`;
}

function fromHtml(html: string): Partial<TextComponent> {
  const parser = new DOMParser();
  const doc = parser.parseFromString(html, "text/html");
  const el = doc.querySelector("ig-text-component");
  if (!el) return {};
  return {
    content: el.innerHTML,
    lineHeight: el.getAttribute("line-height") ?? "1.5",
    letterSpacing: el.getAttribute("letter-spacing") ?? "0",
    padding: el.getAttribute("padding") ?? "0",
    margin: el.getAttribute("margin") ?? "0",
  };
}

export const textComponentHandler: ModHandler<TextComponent> = {
  normalize: (mod) => mod,

  convertFrom: (mod) => ({
    type: "onsiteEdit",
    dto: {
      id: mod.id,
      variationId: mod.variationId,
      querySelectors: [mod.selector],
      onsiteEditType: "html",
      replace: toHtml(mod),
      find: "",
      isRegex: false,
      skip: false,
      remove: false,
      isHtml: false,
      isImage: false,
      insertType: mod.insertType === "replace" ? null : mod.insertType,
      createdAtUrl: mod.createdAtUrl,
      source: mod.source ?? null,
      title: mod.description ?? null,
    },
  }),

  convertTo: (conversion) => {
    if (conversion.type !== "onsiteEdit")
      throw new Error("Expected onsiteEdit");
    const edit = conversion.dto;
    const parsed = fromHtml(edit.replace);
    return {
      type: "textComponent",
      category: "component",
      id: edit.id,
      variationId: edit.variationId,
      selector: edit.querySelectors.join(","),
      insertType: edit.insertType ?? "replace",
      createdAtUrl: edit.createdAtUrl ?? undefined,
      source: edit.source ?? undefined,
      content: parsed.content ?? "",
      lineHeight: parsed.lineHeight ?? "1.5",
      letterSpacing: parsed.letterSpacing ?? "0",
      padding: parsed.padding ?? "0",
      margin: parsed.margin ?? "0",
      description: edit.title ?? undefined,
    };
  },

  getErrors: (mod) => {
    const errors: Record<string, string> = {};
    if (!mod.content) {
      errors.content = "Content is required";
    }
    if (!mod.selector) {
      errors.selector = "Element selection is required";
    }
    if (mod.selector && !isValidSelector(mod.selector)) {
      errors.selector = "Selector is invalid";
    }
    return errors;
  },

  isEquivalent: (a, b) =>
    b.type === "textComponent" &&
    a.selector === b.selector &&
    a.insertType === b.insertType,

  defaults: () =>
    getDefaultText({
      id: v4(),
      variationId: "",
      selector: "",
      insertType: "beforebegin",
    }),

  toAiFormat: (mod) =>
    pickSharedFields<SharedTextComponent>(SharedTextComponentSchema, mod),

  fromAiFormat: (aiMod, context) => ({
    ...(context.existing ?? textComponentHandler.defaults()),
    ...aiMod,
    ...sharedFromAiFormat(aiMod, context),
  }),

  hasChanges: (oldMod, newMod) => {
    const pick = (mod: TextComponent) => ({
      content: mod.content,
      lineHeight: mod.lineHeight,
      letterSpacing: mod.letterSpacing,
      padding: mod.padding,
      margin: mod.margin,
    });
    return !isEqual(pick(oldMod), pick(newMod));
  },
};
